import React from 'react'
import { MapPinIcon } from '@heroicons/react/24/solid'
import PropTypes from 'prop-types'
import { PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline'
import { normalizeSocialLinks } from '../../../utils/social'
import { buildCloudinaryUrl, buildCloudinarySrcSet } from '../../../utils/cloudinaryImage'
import useCardPWA from '../../../pwa/useCardPWA'
import PWAInstallButton from '../../../components/pwa/PWAInstallButton'

function ProfessionalTemplate({ vCard, template }) {
  const theme = vCard.theme || {
    primaryColor: '#2563eb',
    accentColor: '#64748b',
    font: 'Inter'
  }

  const socialLinksRaw = vCard.social || {}
  const socialLinks = normalizeSocialLinks(socialLinksRaw)
  const brandColors = {
    whatsapp: '#25D366',
    facebook: '#1877F2',
    linkedin: '#0A66C2',
    instagram: '#E4405F',
    tiktok: '#000000',
  }
  const phone = Array.isArray(vCard.phone) ? vCard.phone[0] : vCard.phone
  const coverUrl = vCard.coverUrl
  const initials = (vCard.name || '').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase()
  useCardPWA(vCard)

  return (
    <div 
      className="professional-template w-full sm:max-w-md md:max-w-lg lg:max-w-xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden min-h-screen"
      style={{ fontFamily: theme.font }}
    >
      {/* Header with gradient */}
      <div 
        className="relative h-56 md:h-72"
        style={{ backgroundColor: theme.primaryColor, backgroundImage: coverUrl ? `url(${coverUrl})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-6">
          <div className="flex items-end">
            <div className="relative">
              {vCard.photoUrl ? (
                <img
                  src={buildCloudinaryUrl(vCard.photoUrl, { w: 160, h: 160, crop: 'fill' })}
                  srcSet={buildCloudinarySrcSet(vCard.photoUrl, [80, 120, 160])}
                  sizes="(max-width: 768px) 120px, 160px"
                  alt={vCard.name}
                  className="w-24 h-24 md:w-28 md:h-28 rounded-full border-4 border-white shadow-2xl object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-24 h-24 md:w-28 md:h-28 rounded-full border-4 border-white shadow-2xl bg-white flex items-center justify-center text-2xl font-bold text-gray-700">
                  {initials || '—'}
                </div>
              )}
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: theme.accentColor }}></div>
            </div>
            <div className="ml-4 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-white">{vCard.name}</h1>
              <p className="text-sm md:text-base text-white/80">{vCard.position}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Section */}
      <div className="px-6 pb-8">

        {/* Bio */}
        {vCard.bio && (
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 md:p-6 mb-6">
            <p className="text-gray-700 text-sm md:text-base leading-relaxed">
              {vCard.bio}
            </p>
          </div>
        )}

        {/* Contact Info */}
        <div className="space-y-3 mb-6">
          {phone && (
            <a 
              href={`tel:${phone}`}
              className="flex items-center text-gray-700 hover:text-blue-600 transition-colors"
            >
              <PhoneIcon className="w-5 h-5 mr-3" style={{ color: theme.primaryColor }} />
              <span>{phone}</span>
            </a>
          )}
          
          {vCard.email && (
            <a 
              href={`mailto:${vCard.email}`}
              className="flex items-center text-gray-700 hover:text-blue-600 transition-colors"
            >
              <EnvelopeIcon className="w-5 h-5 mr-3" style={{ color: theme.primaryColor }} />
              <span>{vCard.email}</span>
            </a>
          )}
        </div>

        <div className="flex justify-center space-x-4 mb-8">
          {socialLinks.whatsapp && (
            <a
              href={socialLinks.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
              style={{ backgroundColor: brandColors.whatsapp, color: '#fff' }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.645 3.488"/>
              </svg>
            </a>
          )}
          
          {socialLinks.facebook && (
            <a
              href={socialLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
              style={{ backgroundColor: brandColors.facebook, color: '#fff' }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22.675 0h-21.35C.6 0 0 .6 0 1.342v21.317C0 23.4.6 24 1.325 24h11.495v-9.294H9.691v-3.622h3.129V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.794.143v3.24l-1.918.001c-1.504 0-1.796.715-1.796 1.764v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.4 24 24 23.4 24 22.659V1.342C24 .6 23.4 0 22.675 0z"/>
              </svg>
            </a>
          )}

          {socialLinks.linkedin && (
            <a
              href={socialLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
              style={{ backgroundColor: brandColors.linkedin, color: '#fff' }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          )}
          
          {socialLinks.instagram && (
            <a
              href={socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
              style={{ backgroundColor: brandColors.instagram, color: '#fff' }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/>
              </svg>
            </a>
          )}

          {socialLinks.tiktok && (
            <a
              href={socialLinks.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
              style={{ backgroundColor: brandColors.tiktok, color: '#fff' }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.004 0h3.043c.214 1.833 1.097 3.221 2.61 4.17 1.023.647 2.156.953 3.39.934v3.17c-1.257.03-2.44-.208-3.553-.723-.676-.315-1.302-.706-1.876-1.171l-.01 6.681c0 3.73-2.484 6.89-6.138 7.67-3.679.785-7.38-1.184-8.642-4.69-1.29-3.574.28-7.59 3.582-9.335 1.171-.632 2.433-.94 3.785-.93v3.242c-.313-.05-.615-.04-.91.03-1.463.341-2.49 1.676-2.41 3.17.08 1.476 1.253 2.705 2.73 2.818 1.638.126 3.033-.882 3.36-2.494.055-.27.083-.55.083-.83l.01-12.741z"/>
              </svg>
            </a>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 pb-10">
          <button
            className="flex-1 py-3 px-4 rounded-xl font-medium text-white transition-all hover:scale-105"
            style={{ backgroundColor: theme.primaryColor }}
            onClick={() => window.open(`tel:${phone}`, '_self')}
          >
            Llamar
          </button>
          <button
            className="flex-1 py-3 px-4 rounded-xl font-medium text-white transition-all hover:scale-105"
            style={{ backgroundColor: brandColors.whatsapp }}
            onClick={() => window.open(socialLinks.whatsapp, '_blank')}
          >
            WhatsApp
          </button>
        </div>
      </div>
      <PWAInstallButton vCard={vCard} />
    </div>
  )
}

ProfessionalTemplate.propTypes = {
  vCard: PropTypes.object.isRequired,
  template: PropTypes.object.isRequired,
}

export default ProfessionalTemplate
